import type { Player, Question, QuestionResult, RoundState } from './model';

export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  secret?: boolean;
  progress?: (player: Player) => [number, number];
  unlocked: (player: Player, round?: QuestionResult[]) => boolean;
}

export type DecorationSlot = 'wearable' | 'counter' | 'wall' | 'shelf' | 'room' | 'theme';

export interface DecorationDefinition {
  id: string;
  name: string;
  slot: DecorationSlot;
  price: number;
  icon: string;
  description: string;
}

export const ROUND_TOKEN_REWARD = 10;

export const DECORATIONS = [
  { id: 'apron', name: 'Apron', slot: 'wearable', price: 10, icon: '🥣', description: 'A berry-trimmed baking apron.' },
  { id: 'bow', name: 'Bow', slot: 'wearable', price: 10, icon: '🎀', description: 'A bright bow for café days.' },
  { id: 'star-collar', name: 'Star collar', slot: 'wearable', price: 10, icon: '⭐', description: 'A tiny golden star collar.' },
  { id: 'baker-cap', name: 'Baker cap', slot: 'wearable', price: 20, icon: '🧑‍🍳', description: 'A puffy cap for the head baker.' },
  { id: 'neck-scarf', name: 'Neck scarf', slot: 'wearable', price: 20, icon: '🧣', description: 'A cozy golden café scarf.' },
  { id: 'flower-crown', name: 'Flower crown', slot: 'wearable', price: 30, icon: '🌼', description: 'A cheerful crown of little flowers.' },

  { id: 'counter-plant', name: 'Plant', slot: 'counter', price: 10, icon: '🪴', description: 'A leafy friend for the counter.' },
  { id: 'cookie-jar', name: 'Cookie jar', slot: 'counter', price: 10, icon: '🍪', description: 'A glass jar filled with cookies.' },
  { id: 'flower-vase', name: 'Flower vase', slot: 'counter', price: 10, icon: '💐', description: 'Fresh flowers in a blue vase.' },
  { id: 'teapot', name: 'Teapot', slot: 'counter', price: 20, icon: '🫖', description: 'Tea is always ready to pour.' },
  { id: 'cupcake-stand', name: 'Cupcake stand', slot: 'counter', price: 20, icon: '🧁', description: 'A special stand for one perfect cupcake.' },
  { id: 'recipe-book', name: 'Recipe book', slot: 'counter', price: 20, icon: '📖', description: 'The café’s favorite recipes.' },
  { id: 'fruit-bowl', name: 'Fruit bowl', slot: 'counter', price: 20, icon: '🍎', description: 'A colorful bowl of fresh fruit.' },

  { id: 'number-poster', name: 'Number poster', slot: 'wall', price: 10, icon: '🔢', description: 'Even numbers march across the wall.' },
  { id: 'paw-poster', name: 'Paw poster', slot: 'wall', price: 10, icon: '🐾', description: 'Paw prints make a lovely equation.' },
  { id: 'bakery-menu', name: 'Bakery menu', slot: 'wall', price: 20, icon: '🥐', description: 'Today’s treats in café lettering.' },
  { id: 'times-chalkboard', name: 'Times-table chalkboard', slot: 'wall', price: 20, icon: '✖️', description: 'A friendly multiplication chalkboard.' },
  { id: 'garden-print', name: 'Garden print', slot: 'wall', price: 20, icon: '🌿', description: 'A calm green garden scene.' },
  { id: 'cat-portrait', name: 'Cat portrait', slot: 'wall', price: 30, icon: '🖼️', description: 'A grand portrait of a café cat.' },

  { id: 'cat-stuffy', name: 'Cat stuffy', slot: 'shelf', price: 10, icon: '🧸', description: 'A soft little shelf companion.' },
  { id: 'cookie-cushion', name: 'Cookie cushion', slot: 'shelf', price: 10, icon: '🍪', description: 'A round, squishy cookie pillow.' },
  { id: 'cookbooks', name: 'Cookbooks', slot: 'shelf', price: 10, icon: '📚', description: 'A colorful stack of baking books.' },
  { id: 'yarn-basket', name: 'Yarn basket', slot: 'shelf', price: 20, icon: '🧶', description: 'A basket of irresistible yarn.' },
  { id: 'pastry-plush', name: 'Pastry plush', slot: 'shelf', price: 20, icon: '🥐', description: 'A cuddly croissant plushie.' },
  { id: 'tiny-clock', name: 'Tiny clock', slot: 'shelf', price: 30, icon: '🕰️', description: 'A tiny clock that never rushes.' },

  { id: 'bunting', name: 'Bunting', slot: 'room', price: 20, icon: '🔺', description: 'A string of festive café flags.' },
  { id: 'cafe-curtains', name: 'Café curtains', slot: 'room', price: 30, icon: '🪟', description: 'Striped curtains for the big window.' },
  { id: 'hanging-plants', name: 'Hanging plants', slot: 'room', price: 30, icon: '🌱', description: 'Two trailing plants overhead.' },
  { id: 'string-lights', name: 'String lights', slot: 'room', price: 30, icon: '✨', description: 'Warm lights for a cozy glow.' },
  { id: 'paper-lanterns', name: 'Paper lanterns', slot: 'room', price: 40, icon: '🏮', description: 'A row of colorful paper lanterns.' },

  { id: 'berry-theme', name: 'Berry colors', slot: 'theme', price: 30, icon: '🫐', description: 'Rich berry pinks and plum.' },
  { id: 'honey-theme', name: 'Honey & Cream', slot: 'theme', price: 30, icon: '🍯', description: 'Golden honey with warm cream.' },
  { id: 'mint-theme', name: 'Mint Morning', slot: 'theme', price: 30, icon: '🌿', description: 'Fresh mint and soft aqua.' },
  { id: 'blueberry-theme', name: 'Blueberry Night', slot: 'theme', price: 30, icon: '🌙', description: 'Deep blue with lilac details.' },
  { id: 'peach-theme', name: 'Peach Sunrise', slot: 'theme', price: 30, icon: '🍑', description: 'Peach, coral, and morning cream.' },
  { id: 'cocoa-theme', name: 'Cocoa Cozy', slot: 'theme', price: 30, icon: '☕', description: 'Cocoa brown with caramel warmth.' },
] as const satisfies ReadonlyArray<DecorationDefinition>;

export const LEGACY_BADGE_DECORATIONS: Readonly<Record<string, string>> = Object.freeze({
  'first-batch': 'apron',
  'busy-baker': 'bow',
  'hundred-sprinkles': 'berry-theme',
  'cookie-combiner': 'cookie-jar',
  'snack-saver': 'flower-vase',
  'batch-baker': 'number-poster',
  'fair-shares': 'cat-stuffy',
  'double-scoop': 'paw-poster',
  'perfect-ten': 'cookie-cushion',
  'regular-customer': 'counter-plant',
  'full-shift': 'bunting',
});

const opBadge = (id: string, name: string, operation: 'add' | 'subtract' | 'multiply' | 'divide'): BadgeDefinition => ({
  id, name, description: `Serve 25 ${operation} orders independently.`,
  progress: (p) => [p.totals.byOperation[operation], 25],
  unlocked: (p) => p.totals.byOperation[operation] >= 25,
});

export const BADGES: BadgeDefinition[] = [
  { id: 'first-batch', name: 'First Batch', description: 'Answer 10 orders independently.', progress: (p) => [p.totals.independent, 10], unlocked: (p) => p.totals.independent >= 10 },
  { id: 'busy-baker', name: 'Busy Baker', description: 'Answer 50 orders independently.', progress: (p) => [p.totals.independent, 50], unlocked: (p) => p.totals.independent >= 50 },
  { id: 'hundred-sprinkles', name: 'Hundred Sprinkles', description: 'Answer 100 orders independently.', progress: (p) => [p.totals.independent, 100], unlocked: (p) => p.totals.independent >= 100 },
  { id: 'full-house', name: 'Full House', description: 'Answer 250 orders independently.', progress: (p) => [p.totals.independent, 250], unlocked: (p) => p.totals.independent >= 250 },
  { id: 'cafe-legend', name: 'Café Legend', description: 'Answer 500 orders independently.', progress: (p) => [p.totals.independent, 500], unlocked: (p) => p.totals.independent >= 500 },
  opBadge('cookie-combiner', 'Cookie Combiner', 'add'),
  opBadge('snack-saver', 'Snack Saver', 'subtract'),
  opBadge('batch-baker', 'Batch Baker', 'multiply'),
  opBadge('fair-shares', 'Fair Shares', 'divide'),
  { id: 'double-scoop', name: 'Double Scoop', description: 'Solve 5 different nonzero doubles.', progress: (p) => [p.totals.distinctDoubles.length, 5], unlocked: (p) => p.totals.distinctDoubles.length >= 5 },
  { id: 'perfect-ten', name: 'Perfect Ten', description: 'Find 5 different pairs that make 10.', progress: (p) => [p.totals.distinctTens.length, 5], unlocked: (p) => p.totals.distinctTens.length >= 5 },
  { id: 'two-by-two', name: 'Two by Two', description: 'Solve 8 different facts from the 2s table.', progress: (p) => [p.totals.distinctTwos.length, 8], unlocked: (p) => p.totals.distinctTwos.length >= 8 },
  { id: 'clean-plate', name: 'Clean Plate', description: 'Solve 5 different subtraction facts that leave zero.', progress: (p) => [p.totals.distinctZeroSubtractions.length, 5], unlocked: (p) => p.totals.distinctZeroSubtractions.length >= 5 },
  { id: 'second-helping', name: 'Second Helping', description: 'Come back later and solve a fact you once missed.', secret: true, unlocked: (p) => p.totals.recoveredFacts.length > 0 },
  { id: 'house-special', name: 'House Special', description: 'Finish a round using at least 3 operations, with an independent answer in each.', unlocked: (_p, round) => {
    if (!round?.length) return false;
    const ops = new Set(round.filter((r) => r.firstCorrect && !r.helped).map((r) => r.operation));
    return ops.size >= 3;
  } },
  { id: 'grand-opening', name: 'Grand Opening', description: 'Complete your first café round.', progress: (p) => [p.totals.rounds, 1], unlocked: (p) => p.totals.rounds >= 1 },
  { id: 'regular-customer', name: 'Regular Customer', description: 'Complete 5 café rounds.', progress: (p) => [p.totals.rounds, 5], unlocked: (p) => p.totals.rounds >= 5 },
  { id: 'full-shift', name: 'Full Shift', description: 'Complete 20 café rounds.', progress: (p) => [p.totals.rounds, 20], unlocked: (p) => p.totals.rounds >= 20 },
  { id: 'purr-service', name: 'Purr Service', description: 'You found the customer’s ticklish spot!', secret: true, unlocked: (p) => p.badges.includes('purr-service') },
  { id: 'not-a-croissant', name: 'Not a Croissant', description: 'That sleepy shape was a tiny cat!', secret: true, unlocked: (p) => p.badges.includes('not-a-croissant') },
];

export const CATS = [
  { id: 'marmalade', name: 'Marmalade', color: '#e89a45', marking: 'stripes' },
  { id: 'tabby', name: 'Brownie', color: '#aa896b', marking: 'tabby' },
  { id: 'gray-tabby', name: 'Smokey', color: '#92949b', marking: 'tabby' },
  { id: 'tuxedo', name: 'Tux', color: '#3f4050', marking: 'bib' },
  { id: 'calico', name: 'Patches', color: '#f4c68b', marking: 'patches' },
  { id: 'cloud', name: 'Cloud', color: '#e9edf3', marking: 'ears' },
  { id: 'cocoa', name: 'Cocoa', color: '#795548', marking: 'muzzle' },
  { id: 'mint', name: 'Minty', color: '#9dc8b2', marking: 'mask' },
  { id: 'berry', name: 'Berry', color: '#9a6682', marking: 'star' },
  { id: 'sunbeam', name: 'Sunbeam', color: '#f5cf67', marking: 'spots' },
  { id: 'chai', name: 'Chai', color: '#b48564', marking: 'patches' },
  { id: 'bluebell', name: 'Bluebell', color: '#718a9e', marking: 'bib' },
  { id: 'sesame', name: 'Cookie', color: '#c7b89d', marking: 'tabby' },
  { id: 'juniper', name: 'Juniper', color: '#819b85', marking: 'mask' },
  { id: 'mooncake', name: 'Mooncake', color: '#45475b', marking: 'star' },
  { id: 'peaches', name: 'Peaches', color: '#e7a574', marking: 'ears' },
  { id: 'biscuit', name: 'Biscuit', color: '#d4b786', marking: 'spots' },
  { id: 'fig', name: 'Fig', color: '#74566f', marking: 'muzzle' },
  { id: 'socks', name: 'Socks', color: '#777985', marking: 'bib' },
  { id: 'honey', name: 'Honey', color: '#dda84f', marking: 'stripes' },
  { id: 'clover', name: 'Clover', color: '#a9bd91', marking: 'patches' },
  { id: 'ember', name: 'Ember', color: '#ae6548', marking: 'tabby' },
  { id: 'snowdrop', name: 'Snowdrop', color: '#f4efe5', marking: 'ears' },
  { id: 'lavender', name: 'Lavender', color: '#a18cae', marking: 'star' },
  { id: 'mochi', name: 'Mochi', color: '#e5d7c4', marking: 'ears' },
  { id: 'pepper', name: 'Pepper', color: '#5d606c', marking: 'spots' },
  { id: 'pumpkin', name: 'Pumpkin', color: '#d8793e', marking: 'stripes' },
  { id: 'waffles', name: 'Waffles', color: '#d7b16e', marking: 'patches' },
  { id: 'stormy', name: 'Stormy', color: '#69727e', marking: 'tabby' },
  { id: 'poppy', name: 'Poppy', color: '#c97b70', marking: 'star' },
  { id: 'taro', name: 'Taro', color: '#9580a1', marking: 'mask' },
  { id: 'olive', name: 'Olive', color: '#879369', marking: 'muzzle' },
  { id: 'custard', name: 'Custard', color: '#efd083', marking: 'bib' },
  { id: 'maple', name: 'Maple', color: '#ba7b50', marking: 'spots' },
  { id: 'pebble', name: 'Pebble', color: '#9e978e', marking: 'patches' },
  { id: 'inky', name: 'Inky', color: '#363846', marking: 'bib' },
  { id: 'sprout', name: 'Sprout', color: '#9db587', marking: 'ears' },
  { id: 'truffle', name: 'Truffle', color: '#624b43', marking: 'muzzle' },
  { id: 'marzipan', name: 'Marzipan', color: '#e2c69f', marking: 'star' },
  { id: 'comet', name: 'Comet', color: '#70758d', marking: 'spots' },
  { id: 'toffee', name: 'Toffee', color: '#bf8755', marking: 'stripes' },
  { id: 'pickle', name: 'Pickle', color: '#9aab73', marking: 'mask' },
  { id: 'dumpling', name: 'Dumpling', color: '#ddd5ca', marking: 'ears' },
  { id: 'acorn', name: 'Acorn', color: '#89664e', marking: 'tabby' },
  { id: 'plum', name: 'Plum', color: '#865e82', marking: 'star' },
  { id: 'noodle', name: 'Noodle', color: '#c9a47d', marking: 'patches' },
  { id: 'cricket', name: 'Cricket', color: '#59665b', marking: 'bib' },
  { id: 'twinkle', name: 'Twinkle', color: '#e5c469', marking: 'spots' },
  { id: 'saffron', name: 'Saffron', color: '#d99048', marking: 'stripes' },
  { id: 'miso', name: 'Miso', color: '#c89e78', marking: 'muzzle' },
  { id: 'crumpet', name: 'Crumpet', color: '#d8ba8b', marking: 'spots' },
  { id: 'pistachio', name: 'Pistachio', color: '#a9b783', marking: 'mask' },
  { id: 'blueberry', name: 'Blueberry', color: '#697994', marking: 'star' },
  { id: 'butterscotch', name: 'Butterscotch', color: '#d69b4d', marking: 'patches' },
  { id: 'earl-grey', name: 'Earl Grey', color: '#858993', marking: 'tabby' },
  { id: 'shortbread', name: 'Shortbread', color: '#e7ca94', marking: 'bib' },
  { id: 'hazelnut', name: 'Hazelnut', color: '#9b6f53', marking: 'muzzle' },
  { id: 'sherbet', name: 'Sherbet', color: '#e6a27f', marking: 'ears' },
  { id: 'meringue', name: 'Meringue', color: '#f2eadc', marking: 'star' },
  { id: 'pretzel', name: 'Pretzel', color: '#ad7351', marking: 'stripes' },
  { id: 'oatmeal', name: 'Oatmeal', color: '#c7b49a', marking: 'tabby' },
  { id: 'dandelion', name: 'Dandelion', color: '#e4bd55', marking: 'spots' },
  { id: 'rosemary', name: 'Rosemary', color: '#82977d', marking: 'mask' },
  { id: 'nutmeg', name: 'Nutmeg', color: '#9f765f', marking: 'patches' },
  { id: 'chestnut', name: 'Chestnut', color: '#835943', marking: 'bib' },
  { id: 'fritter', name: 'Fritter', color: '#c98b5b', marking: 'spots' },
  { id: 'sorbet', name: 'Sorbet', color: '#d8909d', marking: 'ears' },
  { id: 'pecan', name: 'Pecan', color: '#946a4f', marking: 'tabby' },
  { id: 'taffy', name: 'Taffy', color: '#d7a0ad', marking: 'star' },
  { id: 'brioche', name: 'Brioche', color: '#d8a15f', marking: 'bib' },
  { id: 'latte', name: 'Latte', color: '#b89477', marking: 'muzzle' },
  { id: 'fudge', name: 'Fudge', color: '#674d48', marking: 'patches' },
  { id: 'cobbler', name: 'Cobbler', color: '#bf7656', marking: 'stripes' },
  { id: 'strudel', name: 'Strudel', color: '#c99765', marking: 'spots' },
  { id: 'rye', name: 'Rye', color: '#a2896f', marking: 'tabby' },
  { id: 'matcha', name: 'Matcha', color: '#91a574', marking: 'mask' },
  { id: 'cherry', name: 'Cherry', color: '#bd6f76', marking: 'star' },
  { id: 'mulberry', name: 'Mulberry', color: '#785d77', marking: 'bib' },
  { id: 'clementine', name: 'Clementine', color: '#dc8648', marking: 'ears' },
  { id: 'gelato', name: 'Gelato', color: '#d9c4cb', marking: 'patches' },
  { id: 'pancake', name: 'Pancake', color: '#c9a274', marking: 'muzzle' },
  { id: 'beignet', name: 'Beignet', color: '#dec7a8', marking: 'spots' },
  { id: 'turnip', name: 'Turnip', color: '#c9bea9', marking: 'mask' },
  { id: 'scone', name: 'Scone', color: '#b99069', marking: 'tabby' },
  { id: 'paprika', name: 'Paprika', color: '#b8644d', marking: 'stripes' },
  { id: 'marshmallow', name: 'Marshmallow', color: '#f0e7df', marking: 'ears' },
  { id: 'graham', name: 'Graham', color: '#b58b64', marking: 'bib' },
  { id: 'muffin', name: 'Muffin', color: '#9a7b72', marking: 'patches' },
  { id: 'quince', name: 'Quince', color: '#d2ad61', marking: 'star' },
  { id: 'sundae', name: 'Sundae', color: '#d6b7b7', marking: 'spots' },
  { id: 'caramel', name: 'Caramel', color: '#c7864f', marking: 'muzzle' },
  { id: 'sage', name: 'Sage', color: '#8fa18b', marking: 'mask' },
  { id: 'walnut', name: 'Walnut', color: '#765848', marking: 'tabby' },
  { id: 'kettle', name: 'Kettle', color: '#707783', marking: 'bib' },
  { id: 'pudding', name: 'Pudding', color: '#d8b47b', marking: 'ears' },
  { id: 'jellybean', name: 'Jellybean', color: '#c8829c', marking: 'spots' },
] as const;

export const CAT_BOOK_PAGE_SIZE = 8;

export function catBookPages() {
  const scattered: Array<(typeof CATS)[number] | undefined> = Array(CATS.length);
  CATS.forEach((cat, unlockIndex) => { scattered[(unlockIndex * 13) % CATS.length] = cat; });
  return Array.from({ length: Math.ceil(scattered.length / CAT_BOOK_PAGE_SIZE) }, (_, page) => scattered.slice(page * CAT_BOOK_PAGE_SIZE, (page + 1) * CAT_BOOK_PAGE_SIZE));
}

export function recordIndependentFact(player: Player, q: Question): void {
  const addDistinct = (list: string[], value: string) => { if (!list.includes(value)) list.push(value); };
  if (q.operation === 'add' && q.a === q.b && q.a > 0) addDistinct(player.totals.distinctDoubles, q.factKey);
  if (q.operation === 'add' && q.a + q.b === 10) addDistinct(player.totals.distinctTens, `add10:${Math.min(q.a, q.b)}:${Math.max(q.a, q.b)}`);
  if (q.operation === 'multiply' && q.a === 2) addDistinct(player.totals.distinctTwos, q.factKey);
  if (q.operation === 'subtract' && q.a - q.b === 0) addDistinct(player.totals.distinctZeroSubtractions, q.factKey);
}

export function unlockBadges(player: Player, round?: QuestionResult[]): string[] {
  const unlocked: string[] = [];
  for (const badge of BADGES) {
    const alreadyUnlocked = player.badges.includes(badge.id);
    if (!alreadyUnlocked && badge.unlocked(player, round)) {
      player.badges.push(badge.id);
      unlocked.push(badge.id);
    }
  }
  return unlocked;
}

export function awardRoundCat(player: Player): string | undefined {
  const next = CATS.find((cat) => !player.cats.includes(cat.id));
  if (next) player.cats.push(next.id);
  return next?.id;
}

export function commitRoundRewards(player: Player, round: RoundState): boolean {
  if (round.rewardCommitted) return false;
  player.totals.rounds = Math.min(Number.MAX_SAFE_INTEGER, player.totals.rounds + 1);
  player.tokens = Math.min(Number.MAX_SAFE_INTEGER, player.tokens + ROUND_TOKEN_REWARD);
  round.tokensEarned = ROUND_TOKEN_REWARD;
  round.newBadges = [...new Set([...round.newBadges, ...unlockBadges(player, round.results)])];
  round.newCatId = awardRoundCat(player);
  round.rewardCommitted = true;
  return true;
}

export type PurchaseResult = 'purchased' | 'owned' | 'insufficient' | 'missing';

export function purchaseDecoration(player: Player, id: string): PurchaseResult {
  const decoration = decorationById(id);
  if (!decoration) return 'missing';
  if (player.cosmetics.includes(id)) return 'owned';
  if (player.tokens < decoration.price) return 'insufficient';
  player.tokens -= decoration.price;
  player.cosmetics.push(id);
  return 'purchased';
}

export type ToggleDecorationResult = 'shown' | 'hidden' | 'unowned' | 'missing';

export function toggleDecoration(player: Player, id: string): ToggleDecorationResult {
  const decoration = decorationById(id);
  if (!decoration) return 'missing';
  if (!player.cosmetics.includes(id)) return 'unowned';
  if (player.equipped[decoration.slot] === id) {
    delete player.equipped[decoration.slot];
    return 'hidden';
  }
  player.equipped[decoration.slot] = id;
  return 'shown';
}

export function cafeCustomerCats(player: Player): Array<(typeof CATS)[number]> {
  const available = new Set([player.avatar, ...player.cats]);
  const cats = CATS.filter((cat) => available.has(cat.id));
  return cats.length ? cats : [CATS[0]];
}

export function decorationById(id: string | undefined) {
  return DECORATIONS.find((decoration) => decoration.id === id);
}

export function badgeById(id: string): BadgeDefinition | undefined {
  return BADGES.find((badge) => badge.id === id);
}
